import { useState } from 'react'

import { useRefDetail } from '../../hooks/useRefDetail'
import { RefPopover } from './RefPopover'
import s from './Refs.module.scss'

/**
 * Ein Verweis, wie ihn jede Antwort mitliefert.
 *
 * EINE FORM FÜR ALLE ARTEN — Instrument, Rohstoff, Branche, Land, Bündnis,
 * Marktsegment, Kryptowährung. Vorher trug jede Seite ihre eigene Aufteilung:
 * Meldungen vier getrennte Listen, Einschätzungen und Ereignisse zwei
 * parallele Arrays, die nach Position gepaart wurden. Wer eine neue Art
 * hinzufügte, musste sie an allen Stellen nachziehen.
 */
export interface Ref {
  /** Wie er gespeichert ist: `isin:US0378331005`, `ind:IW:10`, `geo:ISO3166:DE`. */
  ref: string
  /** isin, comm, ind, geo, crypt oder mkt. */
  kind: string | null
  /** Der Anzeigename. LEER heißt: Das Ziel gibt es nicht mehr. */
  name: string | null
  /** Die Kennung für die Detailansicht. Leer, wo es keine gibt. */
  id: number | null
}

/**
 * Baut den Verweis auf ein Instrument, das schon aufgelöst vorliegt.
 *
 * FÜR LISTEN, DIE KEINE `targets` FÜHREN. Watchlist und Instrumentenliste
 * handeln von genau einem Titel je Zeile; sie tragen `isin`, `name` und
 * `instrument_id` einzeln, nicht als Verweis. Damit fehlt ihnen nur die
 * Zusammensetzung — und die steht hier, EINMAL, statt in jeder Seite, die
 * ein Popover haben möchte.
 *
 * Der Enricher liefert an dieser Stelle nichts nach: Name und Kennung sind
 * bereits da, und mehr braucht der Grundriss des Popovers nicht.
 *
 * `instrumentId` DARF FEHLEN. In den Reports ist sie freiwillig — eine
 * Bewertung entsteht auch ohne Stammdatenbezug. Ohne sie wird der Verweis
 * nicht verlinkt und der Sprung auf die Stammdaten faellt weg; alles andere
 * bleibt.
 */
export function instrumentRef(isin: string, name: string,
                              instrumentId: number | null): Ref {
  return { ref: `isin:${isin}`, kind: 'isin', name, id: instrumentId }
}

interface Props {
  refs: Ref[]
  /**
   * Was statt einer leeren Zelle steht. Die Aussage ist je Seite eine andere:
   * Bei einer Einschätzung heißt kein Ziel „Markt“, bei einer Meldung
   * „ohne Bezug“.
   */
  leer: string
  /**
   * Höchstzahl der gezeigten Ziele; der Rest erscheint als „+n“.
   * Ohne Angabe werden alle gezeigt.
   */
  max?: number
}

/**
 * Worauf ein Eintrag zeigt.
 *
 * JEDER VERWEIS KLAPPT AUF, keiner navigiert mehr direkt. Vorher führte ein
 * Instrument auf seine Stammdaten, und die übrigen fünf Arten waren tot —
 * ein Rohstoff, eine Branche, ein Land ließen sich nicht anfassen, weil es
 * für sie keine Detailseite gibt. Jetzt zeigen alle sechs ihre Eckdaten, und
 * das Springen übernimmt ein Knopf im Popover.
 */
export function Refs({ refs, leer, max }: Props) {
  if (refs.length === 0) {
    return <span className={s.leer}>{leer}</span>
  }

  const sichtbar = max === undefined ? refs : refs.slice(0, max)
  const weitere = refs.length - sichtbar.length

  return (
    <span className={s.liste}>
      {sichtbar.map((r, i) => (
        // Der Index gehört zum Schlüssel: Dieselbe ISIN darf zweimal in einer
        // Liste stehen — mehrere Einträge zu einem Ziel sind erlaubt.
        <Verweis key={`${r.ref}-${i}`} verweis={r} />
      ))}
      {weitere > 0 && <span className={s.mehr}>+{weitere}</span>}
    </span>
  )
}

function Verweis({ verweis }: { verweis: Ref }) {
  // DER ZUSTAND LIEGT HIER UND NICHT IM POPOVER: Das Popover kennt keine
  // Endpunkte, es zeigt nur, was es bekommt. Wer nachlädt, muss wissen, wann
  // es aufgeht — deshalb `onOpenChange` und der Haken daneben.
  const [offen, setOffen] = useState(false)
  const { detail } = useRefDetail(verweis, offen)

  // Ohne Namen gibt es das Ziel nicht mehr. Dann steht der Verweis selbst da:
  // „ind:IW:99“ ist die ehrlichere Auskunft als ein leeres Feld, und es ist
  // zugleich der Wert, mit dem sich die Lücke schließen lässt.
  const beschriftung = verweis.name ?? `⚠ ${verweis.ref}`
  const klasse = verweis.name === null
    ? s.offen
    : verweis.kind === 'isin' && verweis.id !== null ? s.verlinkt : s.schlicht

  // KEIN `title` MEHR. Dort stand der volle Verweis; jetzt steht er im
  // Popover, und ein Browser-Tooltip beim Überfahren wäre Doppelung, die
  // sich beim Ansteuern des Auslösers vor das Popover schiebt.
  return (
    <RefPopover detail={detail} className={klasse} onOpenChange={setOffen}>
      {beschriftung}
    </RefPopover>
  )
}
